import { type ReactElement, useEffect } from "react";
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";
import type { ElementProps } from "../../functionalComponents/RenderPage";
import calculateRowsAndColumns from "../../lib/calculateGridRowsAndColumns";
import { type ContentContext, countWords, isTextType } from "../../lib/gridTypes";
import { PageColumns, PageElementsFamily, PageMediaAspectsFamily, PageRows, PageSizeAtom, createLocation } from "../../state/page";

import { CurrentGridClass, SingleColumnInPortrait } from "../../state/ui";
import RenderGrid, { StyledGrid } from "./RenderGrid";

function Grid({
	elements,
}: { elements: ReactElement<ElementProps>[] }): ReactElement<ElementProps> {
	const gridClass = StyledGrid.styledComponentId;
	const pageSize = useRecoilValue(PageSizeAtom);
	const isInLandscape = pageSize.landscape;
	const setCurrentGridClass = useSetRecoilState(CurrentGridClass);
	const setPageColumns = useSetRecoilState(PageColumns);
	const setPageRows = useSetRecoilState(PageRows);
	const singleColumnInPortrait = useRecoilValue(SingleColumnInPortrait);
	// take the page from the elements themselves — the global PageIndex points
	// at the current page, which is wrong while this grid is being pre-rendered
	const pageIndex = elements[0]?.props.pageIndex ?? 1;
	// subscribed (not snapshot-read) so the grid reweighs once images load
	const mediaAspects = useRecoilValue(PageMediaAspectsFamily(pageIndex));

	const getContentContext = useRecoilCallback(
		({ snapshot }) =>
			(): ContentContext => {
				const wordCounts = new Map<number, number>();
				for (const el of elements) {
					if (isTextType(el.props.type)) {
						const key = createLocation(pageIndex, el.props.atomId);
						const loadable = snapshot.getLoadable(PageElementsFamily(key));
						if (loadable.state === "hasValue") {
							wordCounts.set(el.props.atomId, countWords(loadable.getValue().data.content));
						}
					}
				}
				return {
					baseFontSize: pageSize.baseFontSize,
					wordCounts,
					mediaAspects,
					slideWidth: pageSize.width,
					slideHeight: pageSize.height,
				};
			},
		[elements, pageIndex, pageSize.baseFontSize, pageSize.width, pageSize.height, mediaAspects],
	);

	const gridArray = calculateRowsAndColumns(
		elements,
		isInLandscape,
		singleColumnInPortrait,
		isInLandscape ? getContentContext() : undefined,
	);

	// semantic column count (max cells in any row) — col is a track index and
	// can be much larger once rows are LCM-scaled or Tufte-weighted
	const cellsPerRow = new Map<number, number>();
	for (const cell of gridArray) {
		cellsPerRow.set(cell.row, (cellsPerRow.get(cell.row) ?? 0) + 1);
	}
	const numberOfColumns = Math.max(1, ...cellsPerRow.values());
	const numberOfRows = gridArray.reduce((prev, cell) => {
		return cell.row > prev ? cell.row : prev;
	}, 1);

	useEffect(() => {
		setPageColumns(numberOfColumns);
		return () => setPageColumns(0);
	}, [numberOfColumns, setPageColumns]);

	useEffect(() => {
		setCurrentGridClass(gridClass);
	}, [gridClass, setCurrentGridClass]);

	useEffect(() => {
		setPageRows(numberOfRows);
		return () => setPageRows(1);
	}, [numberOfRows, setPageRows]);

	return <RenderGrid gridArray={gridArray} alignCellsAsColumn={false} />;
}

export default Grid;
